import { Product, ProductVariant } from 'types/Product';
import { FriendlySiteName, SiteName } from 'types/Site';
import BrandTypes from 'constants/brand';
import { StockCode } from 'types/ProductResponseType';
import { GetImageUrl } from './media/media-util';
import sanitizeProductName from './product/sanitizeProductName';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(global as any).REDIS_INSTANCE_IS_HEALTHY = true;

export const atgOrigin = (window: Window) =>
  window?.location?.origin.indexOf('local') > -1 ? 'http://127.0.0.1:3002' : '';

export function isOnlyDigitInString(string: string) {
  return /(^\d+$)/.test(string);
}

export function stringContainsOnlyAlphanumericCharacters(string: string) {
  return /^[A-Za-z0-9]*$/.test(string);
}

export function getArticle(string: string) {
  if (!/^[a-zA-Z]/.test(string)) {
    throw new Error('First character is not valid');
  }
  if (/^[a,e,u,i,o]/.test(string.toLowerCase())) {
    return `an ${string}`;
  }
  return `a ${string}`;
}

export function mapStockCodeToSchemaAvailability(stockCode: StockCode | undefined): string | undefined {
  if (!stockCode) {
    return undefined;
  }

  switch (stockCode) {
    case StockCode.OUTOFSTOCK:
    case StockCode.UNAVAILABLE:
      return 'https://schema.org/OutOfStock';
    case StockCode.DCSTOCK:
    case StockCode.DDSTOCKWORKINGDAYS:
    case StockCode.DDSTOCKWEEK:
    case StockCode.DDSTOCKDATE:
    case StockCode.DDSTOCKNEXTDATE:
    case StockCode.DDSTOCK:
      return 'https://schema.org/InStock';
    case StockCode.LOWSTOCK:
      return 'https://schema.org/LimitedAvailability';
    case StockCode.PREORDER:
    case StockCode.HOLDDAY:
    case StockCode.HOLDWEEKS:
    case StockCode.HOLDDATE:
      return 'https://schema.org/PreOrder';
    default:
      return undefined;
  }
}

export const getFriendlySiteName = (site: SiteName): FriendlySiteName => {
  switch (site) {
    case BrandTypes.VERYIE:
      return 'Very Ireland';
    case BrandTypes.LITTLEWOODS:
      return 'Littlewoods';
    default:
      return 'Very';
  }
};

/**
 * Return policy type classification
 */
type ReturnPolicyType = 'NOT_PERMITTED' | 'FINITE_WINDOW' | 'UNLIMITED_WINDOW';

/**
 * Return policy mapping configuration
 */
interface ReturnPolicyConfig {
  type: ReturnPolicyType;
  days?: number;
}

/**
 * Maps returnable keys to their return policy schema configuration
 * Based on analysis of all 12 returnable keys in src/constants/non-returnable.ts
 */
const RETURN_POLICY_MAP: Record<string, ReturnPolicyConfig> = {
  'lainfha.5.gif': { type: 'NOT_PERMITTED' },
  'lainfha17.gif': { type: 'NOT_PERMITTED' },
  'lainfha18.gif': { type: 'NOT_PERMITTED' },
  'lainfha19.gif': { type: 'NOT_PERMITTED' },
  'lainfha2.gif': { type: 'NOT_PERMITTED' },
  'lainfha20.gif': { type: 'NOT_PERMITTED' },
  'lainfha21.gif': { type: 'NOT_PERMITTED' },
  'lainfha3.gif': { type: 'NOT_PERMITTED' },
  'lainfha5.gif': { type: 'NOT_PERMITTED' },
  'lainfha7.gif': { type: 'NOT_PERMITTED' },
  'lainfha8.gif': { type: 'NOT_PERMITTED' },
  'lainfha6.gif': { type: 'FINITE_WINDOW', days: 21 },
};

/**
 * Gets the country code for a given site
 * @param site - The site name
 * @returns The ISO country code (GB or IE)
 */
const getCountryCode = (site: SiteName): string => {
  switch (site) {
    case 'veryie':
      return 'IE';
    case 'very':
    case 'littlewoods':
    default:
      return 'GB';
  }
};

/**
 * Converts a relative URL to an absolute URL using the origin from canonicalUrl
 * @param relativeUrl - The relative URL path
 * @param canonicalUrl - The canonical URL to extract origin from
 * @returns The absolute URL
 */
const makeAbsoluteUrl = (relativeUrl: string, canonicalUrl: string): string => {
  // If already absolute, return as is
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }

  // Extract origin from canonicalUrl and append relativeUrl
  const url = new URL(canonicalUrl);
  return `${url.origin}${relativeUrl}`;
};

/**
 * Validates if a product has all required fields for JSON-LD rendering
 * @param product - The product to validate
 * @returns true if the product has all required fields (name, description, images), false otherwise
 */
export const isProductJsonLdEligible = (product: Product): boolean => {
  // Check name exists and is non-empty after trimming
  if (!product.name || product.name.trim().length === 0) {
    return false;
  }

  // Check description exists and is non-empty after trimming
  if (!product.description || product.description.trim().length === 0) {
    return false;
  }

  // Check images array exists and has at least one image with a valid identifier
  if (!product.images || !Array.isArray(product.images) || product.images.length === 0) {
    return false;
  }

  // Check at least one image has a valid identifier
  return product.images.some((img) => img.identifier && img.identifier.trim().length > 0);
};

export const buildProductJsonLd = (
  product: Product,
  site: SiteName,
  canonicalUrl: string,
): Record<string, unknown> | null => {
  // Validate required fields before building JSON-LD
  if (!isProductJsonLdEligible(product)) {
    return null;
  }

  const { skus, description, name, images, reviews, brand, identification, returnable } = product;

  // Since validation ensures name and description are valid strings, we can safely trim them
  const cleanName = sanitizeProductName(name.trim());
  const cleanDescription = description.trim();

  // Build image array
  const imageUrls =
    images
      ?.map((img) =>
        GetImageUrl({
          identifier: img.identifier,
          template: '$pdp_48x64_x2$',
          format: 'jpg',
          site,
        }),
      )
      .filter((url): url is string => Boolean(url)) ?? [];

  // Check if all variants share the same EAN
  const allEans = skus.map((sku) => sku.ean).filter((ean): ean is string => Boolean(ean));
  const uniqueEans = new Set(allEans);
  const sharedEan = uniqueEans.size === 1 && allEans.length > 0 ? allEans[0] : undefined;

  // Build product schema
  const productSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    '@id': `${canonicalUrl}#productgroup`,
    name: cleanName,
    description: cleanDescription,
    url: canonicalUrl,
    productGroupID: identification.productId,
  };

  // Add images if available
  if (imageUrls.length > 0) {
    productSchema.image = imageUrls.length === 1 ? imageUrls[0] : imageUrls;
  }

  // Add brand if available
  if (brand && brand.trim().length > 0) {
    productSchema.brand = {
      '@type': 'Brand',
      name: brand.trim(),
    };
  }

  // Add gtin on parent if all variants share same EAN
  if (sharedEan) {
    productSchema.gtin = sharedEan;
  }

  // Add aggregateRating if reviews exist
  const total = reviews?.total ?? 0;
  const stars = reviews?.stars;
  if (total > 0 && stars !== undefined) {
    productSchema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(stars),
      reviewCount: String(total),
      bestRating: '5',
      worstRating: '1',
    };
  }

  // Compute variesBy array (only standard schema.org dimensions)
  // Single pass through SKUs for efficiency (important for SSR/SEO)
  // Only include dimensions that have 2+ unique values (they actually "vary")
  const uniqueColors = new Set<string>();
  const uniqueSizes = new Set<string>();

  skus.some((sku) => {
    if (sku.options?.colour) {
      uniqueColors.add(sku.options.colour);
    }
    if (sku.options?.size) {
      uniqueSizes.add(sku.options.size);
    }
    // Early exit: if we already have 2+ colors and sizes, no need to continue
    return uniqueColors.size >= 2 && uniqueSizes.size >= 2;
  });

  const variesBy: string[] = [];
  if (uniqueColors.size > 1) {
    variesBy.push('color');
  }
  if (uniqueSizes.size > 1) {
    variesBy.push('size');
  }
  if (variesBy.length > 0) {
    productSchema.variesBy = variesBy;
  }

  // Build hasVariant array
  const variants = skus.map((sku) => {
    const variant: Record<string, unknown> = {
      '@type': 'Product',
    };

    // Build variant-specific name (product name + color + size)
    const variantNameParts = [cleanName];
    if (sku.options?.colour) {
      variantNameParts.push(sku.options.colour);
    }
    if (sku.options?.size) {
      variantNameParts.push(sku.options.size);
    }
    variant.name = variantNameParts.join(' - ');

    // Add variant-specific images
    if (sku.images && sku.images.length > 0) {
      const variantImageUrls = sku.images
        .map((img) =>
          GetImageUrl({
            identifier: img.identifier,
            template: '$pdp_48x64_x2$',
            format: 'jpg',
            site,
          }),
        )
        .filter((url): url is string => Boolean(url));

      if (variantImageUrls.length > 0) {
        variant.image = variantImageUrls.length === 1 ? variantImageUrls[0] : variantImageUrls;
      }
    } else if (imageUrls.length > 0) {
      // Fallback to product images if variant has no specific images
      variant.image = imageUrls.length === 1 ? imageUrls[0] : imageUrls;
    }

    // Add sku if available
    if (sku.option) {
      variant.sku = sku.option;
    }

    // Add url to variant
    variant.url = canonicalUrl;

    // Add gtin if not shared on parent
    if (!sharedEan && sku.ean) {
      variant.gtin = sku.ean;
    }

    // Add mpn if available
    if (sku.manufacturerPartNumber) {
      variant.mpn = sku.manufacturerPartNumber;
    }

    // Add color if available
    if (sku.options?.colour) {
      variant.color = sku.options.colour;
    }

    // Add size if available
    if (sku.options?.size) {
      variant.size = sku.options.size;
    }

    // Add isVariantOf reference
    variant.isVariantOf = {
      '@id': `${canonicalUrl}#productgroup`,
    };

    // Build offer
    const offer: Record<string, unknown> = {
      '@type': 'Offer',
      url: canonicalUrl,
      itemCondition: 'https://schema.org/NewCondition',
    };

    // Add price if available
    const price = sku.price?.amount?.decimal ?? sku.price?.amount?.unformattedPrice;
    if (price !== undefined) {
      offer.price = price;
    }

    // Add priceCurrency if available
    if (sku.price?.amount?.currencyCode) {
      offer.priceCurrency = sku.price.amount.currencyCode;
    }

    // Add availability if stock code is available
    const availability = mapStockCodeToSchemaAvailability(sku.stock?.code);
    if (availability) {
      offer.availability = availability;
    }

    // Add priceValidUntil if available
    if (sku.price?.validUntil) {
      offer.priceValidUntil = sku.price.validUntil;
    }

    // Add seller
    offer.seller = {
      '@type': 'Organization',
      name: getFriendlySiteName(site),
    };

    // Add return policy if available and key is mapped
    if (returnable?.link?.href && returnable?.key) {
      const policyConfig = RETURN_POLICY_MAP[returnable.key];
      if (policyConfig) {
        const absoluteReturnUrl = makeAbsoluteUrl(returnable.link.href, canonicalUrl);

        if (policyConfig.type === 'NOT_PERMITTED') {
          offer.hasMerchantReturnPolicy = {
            '@type': 'MerchantReturnPolicy',
            returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
            applicableCountry: getCountryCode(site),
            merchantReturnLink: absoluteReturnUrl,
          };
        } else if (policyConfig.type === 'FINITE_WINDOW' && policyConfig.days) {
          offer.hasMerchantReturnPolicy = {
            '@type': 'MerchantReturnPolicy',
            returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
            merchantReturnDays: policyConfig.days,
            applicableCountry: getCountryCode(site),
            merchantReturnLink: absoluteReturnUrl,
          };
        } else if (policyConfig.type === 'UNLIMITED_WINDOW') {
          offer.hasMerchantReturnPolicy = {
            '@type': 'MerchantReturnPolicy',
            returnPolicyCategory: 'https://schema.org/MerchantReturnUnlimitedWindow',
            applicableCountry: getCountryCode(site),
            merchantReturnLink: absoluteReturnUrl,
          };
        }
      }
    }

    variant.offers = offer;
    return variant;
  });

  productSchema.hasVariant = variants;

  return productSchema;
};

export const isLocal =
  process.env.ENVIRONMENT === 'local' || process.env.ENVIRONMENT === 'ci' || process.env.ENVIRONMENT === 'docker';

export const evaluateCachingStatus = () => {
  const cacheEnabled =
    process.env.ENVIRONMENT !== 'ci' &&
    process.env.REDIS_CACHE_ENABLED === 'true' &&
    (global as any).REDIS_INSTANCE_IS_HEALTHY === true;
  return cacheEnabled;
};

export const isNotInStock = (stockCode?: string): boolean => stockCode === 'OUTOFSTOCK' || stockCode === 'UNAVAILABLE';

export const getProductTitle = ({ brand = '', name = '' }): string => `${brand} ${name}`.trim();

export const getValidSiteValue = (site?: string): SiteName => {
  const lowerCaseSite = site?.toLowerCase();

  if (lowerCaseSite === 'veryireland' || lowerCaseSite === 'veryie') {
    return 'veryie';
  }

  if (lowerCaseSite === 'littlewoods') {
    return 'littlewoods';
  }

  return 'very';
};

export const getElasticServiceName = (site: SiteName): string => {
  switch (site) {
    case BrandTypes.VERYIE:
      return 'VeryIE - PDP';
    case BrandTypes.LITTLEWOODS:
      return 'Littlewoods - PDP';
    default:
      return 'Very - PDP';
  }
};

export const getBazaarVoiceUrl = (site: SiteName, environment: string) => {
  switch (site) {
    case BrandTypes.VERYIE:
      return `https://apps.bazaarvoice.com/deployments/littlewoods-ie/main_site/${environment}/en_IE/bv.js`;
    case BrandTypes.LITTLEWOODS:
      return `https://apps.bazaarvoice.com/deployments/littlewoods-gb/main_site/${environment}/en_GB/bv.js`;
    default:
      return `https://apps.bazaarvoice.com/deployments/very-gb/main_site/${environment}/en_GB/bv.js`;
  }
};

export const replaceTitleTagValue = (html: string, newValue = '') =>
  html.replace(/<title>(.*?)<\/title>/, `<title>${newValue}</title>`);

export const getSocialMetaTagsStock = (skus: ProductVariant[]): string => {
  const inStockMessage = 'In stock';
  const awaitingStockMessage = 'Awaiting stock';
  const preOrderMessage = 'Pre-order now';
  const outOfStockMessage = 'Out of stock';
  const inStockStatuses = ['In stock', 'Low stock'];
  const statuses = skus.map((sku) => sku.stock?.primary);
  const validStocks = statuses.filter((stock): stock is string => stock !== undefined);

  if (!validStocks.length) return '';

  const atLeastOneSkuInStock = validStocks.some((el) => inStockStatuses.includes(el));
  const atLeastOneSkuAwaitingStock = validStocks.some((el) => el === awaitingStockMessage);
  const atLeastOneSkuPreOrder = validStocks.some((el) => el === preOrderMessage);

  if (atLeastOneSkuInStock) return inStockMessage;
  if (!atLeastOneSkuInStock && atLeastOneSkuAwaitingStock) return awaitingStockMessage;
  if (!atLeastOneSkuInStock && !atLeastOneSkuAwaitingStock && atLeastOneSkuPreOrder) return preOrderMessage;

  return outOfStockMessage;
};

export const addApmLabels = (labels: Record<string, string | number | undefined>) => {
  if (typeof window !== 'undefined' && window.elasticApm?.addLabels) {
    try {
      const filteredLabels = Object.fromEntries(Object.entries(labels).filter(([, v]) => v !== undefined)) as Record<
        string,
        string | number | boolean
      >;

      window.elasticApm.addLabels(filteredLabels);
    } catch (err) {
      console.warn('Elastic APM label add failed:', err);
    }
  }
};
